import { createSlice } from "@reduxjs/toolkit";
import {
  login,
  register,
  logout,
  getUserInfo,
  otpActivate,
  resendOtp,
  getChatByUserId,
  getUserChatData,
  updateUser,
} from "../Thunks/AuthThunk";
import StompChatService from "../../Services/StompChatService";
import { toast } from "react-toastify";
import { removeCookie } from "../../Utils/cookiesHelper";

const activateOtoSuccessMessage =
  "Your account has been activated successfully.";
const registerSuccessMessage = "Check your email for the activation code.";

export const AuthSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    userChatData: null, // Store user chat data (userId, name, email, etc.)
    token: null,
    chatId: null,
    isLoading: false,
    isLoggedIn: false,
    error: null,
    visitor: null,
    room: null,
    message: null,
    savedAt: null,
  },
  reducers: {
    setIsLoggedIn: (state, action) => {
      state.isLoggedIn = action.payload;
    },
    forceLogout: (state) => {
      state.user = null;
      state.userChatData = null;
      state.token = null;
      state.chatId = null;
      state.isLoggedIn = false;
      state.savedAt = null;
      removeCookie("jwtToken");

      // Disconnect WebSocket on force logout
      StompChatService.disconnect();
    },
    clearError: (state, action) => {
      state.error = null;
    },
    forceLogoutWithClearMessages: (state) => {
      state.user = null;
      state.userChatData = null;
      state.token = null;
      state.chatId = null;
      state.isLoggedIn = false;
      state.savedAt = null;
      removeCookie("jwtToken");

      StompChatService.disconnect();
    },
    setChatId: (state, action) => {
      state.chatId = action.payload;
    },
    updateUserImage: (state, action) => {
      if (state.user) {
        state.user.imageUrl = action.payload;
        state.user.image_url = action.payload; // Keep legacy field for compatibility
      }
    },
    initializeWebSocketConnection: (state) => {
      // Initialize WebSocket connection if token exists but connection is not established
      if (state.token && state.isLoggedIn) {
        const connectionStatus = StompChatService.getConnectionStatus();
        if (!connectionStatus.connected) {
          StompChatService.connect(
            state.token,
            null,
            null, // Message handlers registered via StompChatService.onMessage()
            null
          );
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(login.pending, (state, action) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      state.error = null;
      state.token = action.payload.refresh;
      state.user = action.payload.userInfo;
      state.isLoggedIn = true;
      state.isLoading = false;
      state.savedAt = Date.now();

      // Connect to WebSocket after successful login
      if (action.payload.refresh) {
        StompChatService.connect(
          action.payload.refresh,
          null,
          null, // Message handlers registered via StompChatService.onMessage()
          null
        );
      }
    });
    builder.addCase(login.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    builder.addCase(register.pending, (state, action) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(register.fulfilled, (state, action) => {
      state.error = null;
      state.isLoading = false;
      toast.success(registerSuccessMessage);
    });
    builder.addCase(register.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    builder.addCase(otpActivate.pending, (state, action) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(otpActivate.fulfilled, (state, action) => {
      state.error = null;
      state.isLoading = false;
      toast.success(activateOtoSuccessMessage);
      state.token = action.payload.refresh;
      state.user = action.payload.userInfo;
      state.isLoggedIn = true;
      state.savedAt = Date.now();

      // Connect to WebSocket after successful OTP activation
      if (action.payload.refresh) {
        StompChatService.connect(
          action.payload.refresh,
          null,
          null, // Message handlers registered via StompChatService.onMessage()
          null
        );
      }
    });
    builder.addCase(otpActivate.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload?.message;
    });

    builder.addCase(resendOtp.pending, (state, action) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(resendOtp.fulfilled, (state, action) => {
      state.error = null;
      state.isLoading = false;
      toast.success(registerSuccessMessage);
    });
    builder.addCase(resendOtp.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    builder.addCase(logout.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(logout.fulfilled, (state, action) => {
      state.isLoading = false;
      state.user = null;
      state.token = null;
      state.chatId = null;
      state.isLoggedIn = false;
      state.savedAt = null;
      removeCookie("jwtToken");
      StompChatService.disconnect();
    });
    builder.addCase(logout.rejected, (state, action) => {
      state.isLoading = false;
    });

    builder.addCase(getUserInfo.pending, (state, action) => {});
    builder.addCase(getUserInfo.fulfilled, (state, action) => {
      state.user = action.payload;
    });
    builder.addCase(getUserInfo.rejected, (state, action) => {});

    builder.addCase(getChatByUserId.fulfilled, (state, action) => {
      state.chatId = action.payload.chatId;
    });

    builder.addCase(getUserChatData.fulfilled, (state, action) => {
      state.userChatData = action.payload;
    });

    builder.addCase(updateUser.fulfilled, (state, action) => {
      if (state.user && action.payload?.name) {
        state.user.name = action.payload.name;
      }
    });
  },
});

export const {
  clearError,
  setChatId,
  forceLogoutWithClearMessages,
  initializeWebSocketConnection,
  updateUserImage,
} = AuthSlice.actions;
